import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellOff,
  Check,
  ChevronDown,
  ChevronRight,
  Clipboard,
  RotateCcw,
  ShieldQuestion,
} from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import type {
  TelemetryEvent,
  TelemetryIssue,
  TelemetryIssueDetail,
  TelemetryIssueState,
} from '../../lib/api.js';
import { ago, num } from '../../lib/format.js';
import {
  Badge,
  Button,
  Callout,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  ErrorState,
  Field,
  Panel,
  Spinner,
  Textarea,
  Tooltip,
  useToast,
  type Tone,
} from '../../ui/index.js';
import { formatMs } from './format.js';

/**
 * The worklist. This is the panel the rest of the dashboard exists to feed:
 * "is anybody using this" is a chart, "what is broken" is a queue somebody
 * has to work, and a queue that cannot be triaged from here is no better than
 * grepping a log.
 */
export function IssuesPanel({ hours }: { hours: string }) {
  const q = useQuery({
    queryKey: ['telemetry', 'issues', hours],
    queryFn: () => api.get<{ issues: TelemetryIssue[] }>(`/api/v1/telemetry/issues?hours=${hours}`),
    // A screen nobody is looking at must not poll forever — Refresh (in
    // TelemetryTab) is the only thing that re-runs this.
    refetchInterval: false,
  });

  const issues = q.data?.issues ?? [];

  return (
    <Panel
      title="Issues"
      subtitle="Grouped errors, most recent first. A resolved issue that happens again reopens itself — that is the fix that did not hold."
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState error={q.error} />}
      {q.data && issues.length === 0 && (
        <p className="py-8 text-center text-xs text-ink-tertiary">
          No errors reported in this window. This panel lists them grouped by fingerprint, most
          recent first, the moment one occurs.
        </p>
      )}
      {issues.length > 0 && (
        <ul className="space-y-2">
          {issues
            .slice()
            .sort((a, b) => new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime())
            .map((issue) => (
              <IssueRow key={issue.fingerprint} issue={issue} hours={hours} />
            ))}
        </ul>
      )}
    </Panel>
  );
}

const STATE_TONE: Record<TelemetryIssueState, Tone> = {
  open: 'danger',
  acknowledged: 'warn',
  muted: 'neutral',
  resolved: 'ok',
};

const STATE_LABEL: Record<TelemetryIssueState, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  muted: 'Muted',
  resolved: 'Resolved',
};

function IssueRow({ issue, hours }: { issue: TelemetryIssue; hours: string }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['telemetry', 'issue', issue.fingerprint],
    queryFn: () =>
      api.get<{ issue: TelemetryIssueDetail; events: TelemetryEvent[] }>(
        `/api/v1/telemetry/issues/${issue.fingerprint}`,
      ),
    enabled: open,
    refetchInterval: false,
  });

  // Reopened means "this was declared fixed and it happened again", which is
  // a different fact from "nobody has looked at it yet" — the row must not
  // read the same as a fresh issue, or the recurrence gets lost in the list.
  const reopened = issue.state === 'open' && Boolean(issue.resolvedAt);

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: ['telemetry', 'issues', hours] });
    void qc.invalidateQueries({ queryKey: ['telemetry', 'issue', issue.fingerprint] });
  };

  return (
    <li className="rounded-md border border-line-subtle bg-surface-sunken/40">
      {/*
        A DIV, NOT A BUTTON. The action buttons below (Acknowledge/Mute/
        Resolve) have to live inside this same clickable header — that is
        where the row's actions read best — and a <button> nested inside a
        <button> is invalid HTML that browsers silently unnest, which breaks
        the layout in a way that is easy to miss in a quick look and only
        shows up once a real issue with several actions renders.
      */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className="flex w-full cursor-pointer items-start gap-2 px-3 py-2.5 text-left"
      >
        <span aria-hidden className="mt-0.5 shrink-0 text-ink-tertiary">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={STATE_TONE[issue.state]} dot>
              {STATE_LABEL[issue.state]}
            </Badge>
            {reopened && (
              <Tooltip content="Marked resolved before, then happened again — the fix did not hold.">
                <Badge tone="danger" icon={<ShieldQuestion />}>
                  Reopened
                </Badge>
              </Tooltip>
            )}
            <span className="truncate text-sm font-medium text-ink">{issue.name}</span>
            <Badge tone="neutral">{issue.source}</Badge>
            {issue.release && <Badge tone="neutral">{issue.release}</Badge>}
          </div>
          <p className="mt-1 truncate text-xs text-ink-secondary">{issue.message}</p>
          <p className="mt-1 text-2xs text-ink-tertiary">
            {num(issue.events)} event{issue.events === 1 ? '' : 's'} · first {ago(issue.firstSeenAt)}{' '}
            · last {ago(issue.lastSeenAt)}
          </p>
        </div>
        <span
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <IssueActions issue={issue} onChanged={afterChange} />
        </span>
      </div>

      {open && (
        <div className="border-t border-line-subtle px-3 py-3">
          {detail.isLoading && <Spinner />}
          {detail.error && <ErrorState error={detail.error} />}
          {detail.data && (
            <IssueDetail issue={detail.data.issue} events={detail.data.events} />
          )}
        </div>
      )}
    </li>
  );
}

function IssueDetail({ issue, events }: { issue: TelemetryIssueDetail; events: TelemetryEvent[] }) {
  const toast = useToast();

  const copy = () => {
    void navigator.clipboard
      .writeText(issueSummaryText(issue))
      .then(() => toast.success('Issue summary copied'))
      .catch(() => toast.error('Could not copy — select the stack below and copy manually'));
  };

  return (
    <div className="space-y-3">
      {issue.note && (
        <Callout tone="neutral" title="Note">
          {issue.note}
        </Callout>
      )}

      <div>
        <div className="mb-1 flex items-center justify-between">
          <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
            Sample stack
          </h4>
          <Button size="xs" variant="secondary" onClick={copy}>
            <Clipboard aria-hidden className="h-3 w-3" />
            Copy for Claude
          </Button>
        </div>
        {issue.stack ? (
          <pre className="max-h-64 overflow-auto rounded-md bg-surface-sunken p-2.5 font-mono text-2xs leading-relaxed text-ink-secondary">
            {issue.stack}
          </pre>
        ) : (
          <p className="text-2xs text-ink-tertiary">No stack was captured for this issue.</p>
        )}
      </div>

      <div>
        <h4 className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
          Last events
        </h4>
        {events.length === 0 ? (
          <p className="text-2xs text-ink-tertiary">No individual events retained for this window.</p>
        ) : (
          <ul className="space-y-1">
            {events.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-1.5 text-2xs text-ink-secondary">
                <span className="text-ink-tertiary">{ago(e.at)}</span>
                <Badge tone="neutral" size="sm">
                  {e.source}/{e.kind}
                </Badge>
                <span>{e.name}</span>
                {e.status !== null && <span className="nums text-ink-tertiary">status {e.status}</span>}
                {e.durationMs !== null && (
                  <span className="nums text-ink-tertiary">{formatMs(e.durationMs)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Plain text, no markdown fences: this is meant to be pasted straight into a
 * chat session and worked on, and a code fence around the whole thing is one
 * more thing to strip before the model reads it as an issue rather than as a
 * literal block of Markdown.
 */
function issueSummaryText(issue: TelemetryIssueDetail): string {
  const lines = [
    `fingerprint: ${issue.fingerprint}`,
    `${issue.name}: ${issue.message}`,
    `source: ${issue.source}${issue.release ? ` · release ${issue.release}` : ''}`,
    `${num(issue.events)} events · first seen ${issue.firstSeenAt} · last seen ${issue.lastSeenAt}`,
    `state: ${issue.state}`,
  ];
  if (issue.note) lines.push(`note: ${issue.note}`);
  lines.push('', 'stack:', issue.stack ?? '(none captured)');
  return lines.join('\n');
}

/** Which transitions make sense from the current state. */
function nextActions(state: TelemetryIssueState): { target: TelemetryIssueState; label: string; icon: React.ReactNode }[] {
  switch (state) {
    case 'open':
      return [
        { target: 'acknowledged', label: 'Acknowledge', icon: <Check className="h-3 w-3" /> },
        { target: 'muted', label: 'Mute', icon: <BellOff className="h-3 w-3" /> },
        { target: 'resolved', label: 'Resolve', icon: <Check className="h-3 w-3" /> },
      ];
    case 'acknowledged':
      return [
        { target: 'muted', label: 'Mute', icon: <BellOff className="h-3 w-3" /> },
        { target: 'resolved', label: 'Resolve', icon: <Check className="h-3 w-3" /> },
      ];
    case 'muted':
    case 'resolved':
      return [{ target: 'open', label: 'Reopen', icon: <RotateCcw className="h-3 w-3" /> }];
  }
}

function IssueActions({ issue, onChanged }: { issue: TelemetryIssue; onChanged: () => void }) {
  return (
    <div className="flex items-center gap-1">
      {nextActions(issue.state).map((a) => (
        <StateChangeDialog
          key={a.target}
          fingerprint={issue.fingerprint}
          target={a.target}
          label={a.label}
          icon={a.icon}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function StateChangeDialog({
  fingerprint,
  target,
  label,
  icon,
  onChanged,
}: {
  fingerprint: string;
  target: TelemetryIssueState;
  label: string;
  icon: React.ReactNode;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const toast = useToast();

  const mutate = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/telemetry/issues/${fingerprint}`, {
        state: target,
        note: note.trim() === '' ? undefined : note.trim(),
      }),
    onSuccess: () => {
      onChanged();
      setNote('');
      setOpen(false);
      toast.success(`Marked ${STATE_LABEL[target].toLowerCase()}`);
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'That could not be saved'),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="xs" variant="secondary">
          {icon}
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent title={`${label} this issue?`} width="sm">
        <Field label="Note (optional)" hint="Recorded with the change — why, or what was done about it.">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. deployed a fix in 4a1c2f, watching for recurrence"
          />
        </Field>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button loading={mutate.isPending} onClick={() => mutate.mutate()}>
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
